<script setup lang="ts">
import { computed, ref, onMounted, onBeforeUnmount, nextTick, watch } from "vue"
import { useI18n } from "vue-i18n"
import twemoji from "@twemoji/api"
import { useLocaleStore } from "@/store/localeStore"
import { SUPPORTED_LOCALES, type LocaleCode } from "@/i18n"

const { t } = useI18n()

// Render the emoji flags as Twemoji SVG <img>s so they keep their nice "waving"
// look AND show up on Windows/Chrome (which has no regional-indicator emoji
// font and would otherwise render blank). Re-run after the trigger/menu render
// and whenever the current language changes.
const parseFlags = (): void => {
	if (!rootEl.value) return
	twemoji.parse(rootEl.value, {
		folder: "svg",
		ext: ".svg",
		className: "lang-select__twemoji",
		base: "https://cdn.jsdelivr.net/gh/jdecked/twemoji@latest/assets/",
	})
}

const props = withDefaults(defineProps<{
	/** "compact" — flag + 2-letter code (header). "large" — flag + native name (auth pages). */
	size?: "compact" | "large"
}>(), { size: "compact" })

const localeStore = useLocaleStore()
const open  = ref(false)
const rootEl = ref<HTMLElement | null>(null)
const listEl = ref<HTMLUListElement | null>(null)
const focusIdx = ref<number>(-1)

const current = computed(() => localeStore.meta)
const items   = SUPPORTED_LOCALES

const pick = async (code: LocaleCode) => {
	localeStore.setLocale(code)
	open.value = false
	await nextTick()
	parseFlags()
}

const toggle = async () => {
	open.value = !open.value
	if (open.value) {
		focusIdx.value = items.findIndex((l) => l.code === localeStore.current)
		await nextTick()
		parseFlags()
		focusListItem()
	}
}

const focusListItem = () => {
	const el = listEl.value?.querySelectorAll<HTMLLIElement>("[role='option']")[focusIdx.value]
	el?.focus()
}

const onKeydown = (e: KeyboardEvent) => {
	if (!open.value) {
		if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
			e.preventDefault()
			toggle()
		}
		return
	}
	switch (e.key) {
		case "Escape":    open.value = false; (rootEl.value?.querySelector("button") as HTMLButtonElement)?.focus(); break
		case "ArrowDown": e.preventDefault(); focusIdx.value = (focusIdx.value + 1) % items.length; focusListItem(); break
		case "ArrowUp":   e.preventDefault(); focusIdx.value = (focusIdx.value - 1 + items.length) % items.length; focusListItem(); break
		case "Enter":
		case " ":         e.preventDefault(); pick(items[focusIdx.value].code); break
	}
}

const onDocClick = (e: MouseEvent) => {
	if (!rootEl.value) return
	if (!rootEl.value.contains(e.target as Node)) open.value = false
}

onMounted(async () => {
	document.addEventListener("click", onDocClick)
	await nextTick()
	parseFlags()
})
onBeforeUnmount(() => document.removeEventListener("click", onDocClick))

// Re-parse when the language changes (the trigger flag updates).
watch(() => localeStore.current, async () => {
	await nextTick()
	parseFlags()
})
</script>

<template>
	<div
		ref="rootEl"
		class="lang-select"
		:class="[`lang-select--${size}`, { 'lang-select--open': open }]"
		@keydown="onKeydown"
	>
		<button
			type="button"
			class="lang-select__trigger"
			:aria-expanded="open"
			:aria-haspopup="'listbox'"
			:aria-label="t('components.languageSelect.aria_label', { name: current.englishName })"
			@click.stop="toggle"
		>
			<span class="lang-select__flag" aria-hidden="true">{{ current.flag }}</span>
			<span class="lang-select__label">
				{{ size === "compact" ? current.code.toUpperCase() : current.name }}
			</span>
			<svg class="lang-select__caret" :class="{ 'lang-select__caret--up': open }" viewBox="0 0 12 8" aria-hidden="true">
				<path d="M1 1l5 5 5-5" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
			</svg>
		</button>

		<transition name="lang-select__fade">
			<ul
				v-if="open"
				ref="listEl"
				class="lang-select__menu"
				role="listbox"
				:aria-label="t('components.languageSelect.menu_label')"
			>
				<li
					v-for="(loc, i) in items"
					:key="loc.code"
					role="option"
					tabindex="-1"
					:aria-selected="loc.code === current.code"
					class="lang-select__option"
					:class="{ 'is-active': loc.code === current.code, 'is-focused': i === focusIdx }"
					@click="pick(loc.code)"
					@mouseenter="focusIdx = i"
				>
					<span class="lang-select__flag" aria-hidden="true">{{ loc.flag }}</span>
					<span class="lang-select__option-name">{{ loc.name }}</span>
					<span class="lang-select__option-code">{{ loc.code.toUpperCase() }}</span>
					<svg v-if="loc.code === current.code" class="lang-select__check" viewBox="0 0 14 14" aria-hidden="true">
						<path d="M2 7.5l3.2 3.2L12 4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
				</li>
			</ul>
		</transition>
	</div>
</template>

<style scoped lang="scss">
.lang-select {
	position: relative;
	font-feature-settings: "tnum";

	&__trigger {
		display: inline-flex;
		align-items: center;
		gap: 0.8rem;
		height: 4rem;
		padding: 0 1.2rem;
		border: 1px solid var(--gs-400, #e2e4ea);
		border-radius: 0.6rem;
		background: var(--bg-primary, #fff);
		color: var(--gs-100, #111);
		cursor: pointer;
		transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;

		&:hover    { background: var(--bg-secondary, #f4f5f8); }
		&:focus-visible {
			outline: none;
			border-color: var(--primary, #2b6cff);
			box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.12);
		}
	}

	&__flag {
		font-size: 1.8rem;
		line-height: 1;
		display: inline-flex;
		align-items: center;
	}

	// Twemoji renders the flag emoji as an <img>; size it like the text glyph.
	:deep(.lang-select__twemoji) {
		height: 1.8rem;
		width: auto;
		vertical-align: middle;
	}

	&__label {
		font-size: 1.3rem;
		font-weight: 600;
		letter-spacing: 0.02em;
	}

	&__caret {
		width: 1rem;
		height: 0.7rem;
		color: var(--gs-300, #8a8f9c);
		transition: transform 0.18s ease;

		&--up { transform: rotate(180deg); }
	}

	&__menu {
		position: absolute;
		top: calc(100% + 0.6rem);
		inset-inline-end: 0;
		min-width: 22rem;
		padding: 0.6rem;
		margin: 0;
		list-style: none;
		background: var(--bg-primary, #fff);
		border: 1px solid var(--gs-400, #e2e4ea);
		border-radius: 0.8rem;
		box-shadow:
			0 1.2rem 2.8rem rgba(15, 23, 42, 0.10),
			0 0.2rem 0.6rem rgba(15, 23, 42, 0.06);
		z-index: 60;
	}

	&__option {
		display: grid;
		grid-template-columns: auto 1fr auto auto;
		align-items: center;
		gap: 1.2rem;
		padding: 1rem 1.2rem;
		border-radius: 0.6rem;
		cursor: pointer;
		color: var(--gs-100, #111);
		outline: none;
		transition: background 0.12s ease;

		&.is-focused,
		&:hover           { background: var(--bg-secondary, #f4f5f8); }
		&.is-active       { background: rgba(43, 108, 255, 0.08); }
		&:focus-visible   { box-shadow: 0 0 0 2px rgba(43, 108, 255, 0.4); }
	}

	&__option-name {
		font-size: 1.4rem;
		font-weight: 500;
	}

	&__option-code {
		font-size: 1.1rem;
		font-weight: 600;
		letter-spacing: 0.06em;
		color: var(--gs-300, #8a8f9c);
	}

	&__check {
		width: 1.4rem;
		height: 1.4rem;
		color: var(--primary, #2b6cff);
	}

	// Large variant — bigger flag, full name in trigger.
	&--large &__trigger { height: 4.8rem; padding: 0 1.6rem; gap: 1.2rem; }
	&--large &__flag    { font-size: 2.2rem; }
	&--large &__label   { font-size: 1.5rem; }
}

// Transition.
.lang-select__fade-enter-active,
.lang-select__fade-leave-active {
	transition: opacity 0.15s ease, transform 0.15s ease;
}
.lang-select__fade-enter-from,
.lang-select__fade-leave-to {
	opacity: 0;
	transform: translateY(-0.4rem);
}
</style>
